import type { AxiosInstance, AxiosResponse } from 'axios';
import { isAxiosError } from 'axios';
import type { Store } from '@reduxjs/toolkit';
import { NetworkRequestState, sessionExpired } from 'c-data-layer';
import type { CommonState } from 'c-types';

/**
 * Holds every request that gets a 401 while the user is logged in, flags the session as expired
 * (which opens `SessionExpiredDialog`) and, once the user has logged back in, sends the request
 * again and resolves with that response. The caller never sees the 401, so a save made just after
 * the session lapsed goes through after re-login. If the session ends instead (logout), the request
 * fails with its original 401.
 *
 * Not held: anything while logged out or while the session check on load runs (that 401 is how a
 * stale persisted login is noticed), and the requests `isAuthRequest` names (login itself: a wrong
 * password is a 401 too). Returns a function that removes the interceptor.
 */
const retryAfterRelogin = (
    axios: AxiosInstance,
    store: Store<CommonState>,
    isAuthRequest: (url: string | undefined) => boolean,
) => {
    const id = axios.interceptors.response.use(undefined, (error: unknown) => {
        if (!isAxiosError(error) || error.response?.status !== 401 || error.config == null) {
            return Promise.reject(error);
        }
        const { auth } = store.getState();
        if (
            auth.authenticatedUser == null ||
            auth.validatingUserSession.state === NetworkRequestState.InProgress ||
            isAuthRequest(error.config.url)
        ) {
            return Promise.reject(error);
        }

        const { config } = error;
        store.dispatch(sessionExpired());

        return new Promise<AxiosResponse>((resolve, reject) => {
            const unsubscribe = store.subscribe(() => {
                const { auth: next } = store.getState();
                if (next.authenticatedUser == null) {
                    unsubscribe();
                    reject(error);
                } else if (!next.sessionTimedOut) {
                    unsubscribe();
                    resolve(axios.request(config));
                }
            });
        });
    });

    return () => axios.interceptors.response.eject(id);
};

export default retryAfterRelogin;
